import * as React from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Search } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Banner } from "@/components/ui/banner";
import { FormField } from "@/components/ui/form-field";
import { Input } from "@/components/ui/input";
import { SearchableCombobox } from "@/components/ui/searchable-combobox";
import { ClientNameReveal } from "@/components/clients/ClientNameReveal";
import { SyncLedger } from "@/components/pwa/SyncLedger";
import { NewEvaluationButton } from "@/routes/evaluations/NewEvaluationButton";
import { useListEvaluations, useEvaluationStatusCount } from "@/hooks/queries/useListEvaluations";
import { useListClients } from "@/hooks/queries/useListClients";
import { useSubscriptionState, isSubscriptionLapsed } from "@/hooks/queries/useSubscriptionState";
import { lapsedWriteTooltip } from "@/lib/subscription-copy";
import type { EvaluationListItem, EvaluationState } from "@/hooks/queries/evaluation-types";
import type { ClientRecord } from "@/hooks/queries/client-types";
import {
  EVALUATION_STATUS_META,
  EVALUATION_ACTION,
  STATUS_FILTER_OPTIONS,
  humanizeCurrentStep,
} from "@/lib/evaluation-status";
import { formatShortDate } from "@/lib/format-date";

// Evaluations list (UX Screen 12, Story 6.9). The clinician's caseload of draft /
// ready-to-score / completed AMPS evaluations, consuming the Story 6.10 list endpoint
// via `useListEvaluations`. The Client column is a join against `useListClients` + the
// shared per-row reveal (names are never bulk-decrypted); search is client-side only
// (no server text search exists — FR12-deferred); the status filter re-queries
// server-side. Tokens only, no pure-white background, tabular-nums on the numeric/date
// columns, status pills paired with explicit text.

const PAGE_LIMIT = 20;
// A best-effort single page of clients to resolve the Client column. A client beyond
// this page falls back to UUID-only until revealed (reveal works by id regardless).
//
// Known gap (PHI-safe): for a >100-client caseload, an evaluation whose client is off
// this page gets `initialErased={false}`, so an already-erased client is NOT shown the
// tombstone up front (unlike ClientsListScreen, where every row's record is on its own
// page). This never discloses PHI — Reveal still returns 410/ADMIN-004 and latches the
// row to the terminal tombstone on click. Accepted for 6.9 (frontend-only; the list
// endpoint exposes no batch client-by-ids lookup); revisit if large caseloads make the
// missing up-front tombstone a real UX issue (e.g. fetch enough clients to cover the
// current evaluation page).
const CLIENT_JOIN_LIMIT = 100;

// Mock lapsed copy (Screen 12) — the exact contract string.
const LAPSED_BANNER =
  "Read-only access only. Existing evaluation records stay available, but new evaluations cannot be created until the subscription is renewed.";

export function EvaluationsListScreen() {
  const [page, setPage] = React.useState(1);
  const [statusFilter, setStatusFilter] = React.useState<EvaluationState | undefined>(undefined);
  const [search, setSearch] = React.useState("");
  // Revealed names mirrored here transiently so the client-side search can match them
  // (display still lives in each cell). Never persisted, never logged (PHI).
  const [revealedNames, setRevealedNames] = React.useState<Record<string, string>>({});

  // Story 7.6 — the page hosts two tabs: the existing list ("Evaluations", default, unchanged) and
  // the read-only Sync Ledger ("Submitted"). Hand-rolled accessible tablist (no Tabs UI primitive).
  const [activeTab, setActiveTab] = React.useState<TabId>("evaluations");

  const subscription = useSubscriptionState();
  const lapsed = isSubscriptionLapsed(subscription);
  // gh-568 — source-aware blocked-write reason (org seat → contact the admin).
  const lapsedTooltip = lapsedWriteTooltip(
    subscription,
    "Renew the subscription to continue this evaluation",
  );

  const listQuery = useListEvaluations({
    page,
    limit: PAGE_LIMIT,
    status: statusFilter ? [statusFilter] : undefined,
  });
  const { data, isPending, isError, refetch, isFetching } = listQuery;

  // KPI counts — three cheap count-only queries (limit=1 per status).
  const draftCount = useEvaluationStatusCount("draft");
  const submittedCount = useEvaluationStatusCount("submitted");
  const scoredCount = useEvaluationStatusCount("scored");

  // Client join map (id → record) for the Client column's erased-state handling.
  const clientsQuery = useListClients({ limit: CLIENT_JOIN_LIMIT });
  const clientsById = React.useMemo(() => {
    const map = new Map<string, ClientRecord>();
    for (const c of clientsQuery.data?.results ?? []) map.set(c.id, c);
    return map;
  }, [clientsQuery.data]);

  const count = data?.count ?? 0;
  const totalPages = count > 0 ? Math.ceil(count / PAGE_LIMIT) : 1;
  const rows = data?.results ?? [];

  // Clamp to a valid page once settled data lands (mirrors ClientsListScreen).
  React.useEffect(() => {
    if (data && page > totalPages) setPage(totalPages);
  }, [data, page, totalPages]);

  // Client-side search over the LOADED page: by client UUID and by any revealed name.
  const query = search.trim().toLowerCase();
  const filteredRows = query
    ? rows.filter((row) => {
        if (row.client_id.toLowerCase().includes(query)) return true;
        const name = revealedNames[row.client_id];
        return name ? name.toLowerCase().includes(query) : false;
      })
    : rows;

  const onRevealed = React.useCallback(
    (clientId: string, name: { first: string; last: string }) => {
      setRevealedNames((prev) => ({ ...prev, [clientId]: `${name.first} ${name.last}` }));
    },
    [],
  );

  const onStatusChange = (next: EvaluationState | undefined) => {
    setStatusFilter(next);
    setPage(1);
    // Clear the client-side search too: it filters the LOADED page, so a stale term
    // carried into a freshly-queried status would render "no match" even when that
    // status has rows (the new page rarely contains the old search target). Switching
    // filter is a fresh start — the search box resets with it.
    setSearch("");
  };

  return (
    <section className="flex flex-col gap-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="display-xs text-[color:var(--text-primary)]">Evaluations</h1>
          <p className="text-sm text-[color:var(--text-tertiary)]">
            Track draft, in-progress, and completed AMPS evaluations across your active caseload.
          </p>
        </div>
        <NewEvaluationButton />
      </header>

      <EvaluationTabs activeTab={activeTab} onTabChange={setActiveTab} />

      {activeTab === "submitted" ? (
        <div
          role="tabpanel"
          id="panel-submitted"
          aria-labelledby="tab-submitted"
          tabIndex={0}
          className="focus-visible:outline-none"
        >
          <SyncLedger />
        </div>
      ) : (
        <div
          role="tabpanel"
          id="panel-evaluations"
          aria-labelledby="tab-evaluations"
          tabIndex={0}
          className="flex flex-col gap-6 focus-visible:outline-none"
        >
      {lapsed ? (
        <Banner variant="warn" title="Read-only access only.">
          {LAPSED_BANNER}
        </Banner>
      ) : null}

      {/* 3 KPI tiles — counts of each canonical state. */}
      <div
        className="grid grid-cols-1 gap-4 sm:grid-cols-3"
        role="group"
        aria-label="Evaluation counts"
      >
        <KpiTile label="Drafts" query={draftCount} />
        <KpiTile label="Ready to score" query={submittedCount} />
        <KpiTile label="Completed" query={scoredCount} />
      </div>

      {/* Controls: client-side search + server-side status filter. */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField
          label="Search by client / client ID"
          htmlFor="evaluation-search"
          helper="Searches the current page by client ID and any names you've revealed."
        >
          <div className="relative">
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[color:var(--text-placeholder)]"
            />
            <Input
              id="evaluation-search"
              type="search"
              placeholder="Search this page"
              className="pl-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </FormField>

        <FormField label="Status" htmlFor="evaluation-status-filter">
          <SearchableCombobox
            id="evaluation-status-filter"
            options={STATUS_FILTER_OPTIONS.filter((o) => o.value !== undefined).map((o) => ({
              value: o.value as string,
              label: o.label,
            }))}
            value={statusFilter ?? ""}
            onChange={(v) => onStatusChange((v || undefined) as EvaluationState | undefined)}
            placeholder="All statuses"
            searchAriaLabel="Filter by status"
            showSearch={false}
          />
        </FormField>
      </div>

      {isError ? (
        <Banner variant="danger" title="Couldn't load evaluations">
          <div className="flex flex-col gap-2">
            <span>We couldn't load your evaluations. Please try again.</span>
            <span>
              <Button variant="secondary" size="sm" onClick={() => void refetch()}>
                Try again
              </Button>
            </span>
          </div>
        </Banner>
      ) : null}

      {/* Body: loading / empty / no-match / table. */}
      {isPending ? (
        <div
          className="flex items-center justify-center rounded-2xl border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)] px-6 py-12 text-sm text-[color:var(--text-tertiary)]"
          aria-busy="true"
        >
          Loading evaluations…
        </div>
      ) : count === 0 && !isError ? (
        <EmptyState filtered={Boolean(statusFilter)} />
      ) : filteredRows.length === 0 ? (
        <div className="rounded-2xl border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)] px-6 py-12 text-center text-sm text-[color:var(--text-tertiary)]">
          No evaluations on this page match your search.
        </div>
      ) : (
        <>
          <EvaluationsTable
            rows={filteredRows}
            clientsById={clientsById}
            lapsed={lapsed}
            lapsedTooltip={lapsedTooltip}
            onRevealed={onRevealed}
          />
          <Pagination
            page={page}
            totalPages={totalPages}
            hasPrevious={Boolean(data?.previous)}
            hasNext={Boolean(data?.next)}
            isFetching={isFetching}
            onPrevious={() => setPage((p) => Math.max(1, p - 1))}
            onNext={() => setPage((p) => p + 1)}
          />
        </>
      )}
        </div>
      )}
    </section>
  );
}

type TabId = "evaluations" | "submitted";

const TABS: ReadonlyArray<{ id: TabId; label: string }> = [
  { id: "evaluations", label: "Evaluations" },
  { id: "submitted", label: "Submitted" },
];

/**
 * Accessible two-tab control (Story 7.6 AC1). Hand-rolled (the project has no Tabs primitive):
 * native tablist semantics, roving `tabIndex`, Left/Right + Home/End focus navigation, and
 * Enter/Space (native button) activation. Selection follows neither focus alone nor click alone —
 * arrows move focus across tabs; activating (click / Enter / Space) selects.
 */
function EvaluationTabs({
  activeTab,
  onTabChange,
}: {
  activeTab: TabId;
  onTabChange: (id: TabId) => void;
}) {
  // Keyed by `tab.id` (not array position) so reordering TABS can never silently break arrow/Home/End
  // navigation — focus targets resolve by identity, not by index.
  const tabRefs = React.useRef<Partial<Record<TabId, HTMLButtonElement | null>>>({});

  const onKeyDown = (e: React.KeyboardEvent, index: number) => {
    let next = index;
    if (e.key === "ArrowRight") next = (index + 1) % TABS.length;
    else if (e.key === "ArrowLeft") next = (index - 1 + TABS.length) % TABS.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = TABS.length - 1;
    else return;
    e.preventDefault();
    tabRefs.current[TABS[next].id]?.focus();
  };

  return (
    <div
      role="tablist"
      aria-label="Evaluation views"
      className="flex gap-1 border-b border-[color:var(--border-primary)]"
    >
      {TABS.map((tab, index) => {
        const selected = tab.id === activeTab;
        return (
          <button
            key={tab.id}
            ref={(el) => {
              tabRefs.current[tab.id] = el;
            }}
            type="button"
            role="tab"
            id={`tab-${tab.id}`}
            aria-selected={selected}
            aria-controls={`panel-${tab.id}`}
            tabIndex={selected ? 0 : -1}
            onClick={() => onTabChange(tab.id)}
            onKeyDown={(e) => onKeyDown(e, index)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--focus-ring)] ${
              selected
                ? "border-[color:var(--bg-brand-solid)] text-[color:var(--text-brand-secondary)]"
                : "border-transparent text-[color:var(--text-tertiary)] hover:text-[color:var(--text-secondary)]"
            }`}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

function KpiTile({
  label,
  query,
}: {
  label: string;
  query: ReturnType<typeof useEvaluationStatusCount>;
}) {
  // "—" (not a false 0) until a load has succeeded — i.e. while loading and on an
  // initial-load error (no data yet) — so a failed count never reads as zero. After a
  // success, a transient background-refetch error keeps the last good count (React Query
  // retains `data`), which is preferable to flashing "—".
  const value = query.data ? query.data.count : "—";
  return (
    <div className="rounded-2xl border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)] px-5 py-4">
      <div className="text-sm text-[color:var(--text-tertiary)]">{label}</div>
      <div className="tabular-nums text-2xl font-semibold text-[color:var(--text-primary)]">
        {value}
      </div>
    </div>
  );
}

function EmptyState({ filtered }: { filtered: boolean }) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-2xl border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)] px-6 py-12 text-center">
      <p className="text-md text-[color:var(--text-secondary)]">
        {filtered
          ? "No evaluations match this status filter."
          : "No evaluations yet — start one from a client record."}
      </p>
      {!filtered ? (
        <Button asChild variant="primary">
          <Link to="/clients?intent=new-eval">New evaluation</Link>
        </Button>
      ) : null}
    </div>
  );
}

function EvaluationsTable({
  rows,
  clientsById,
  lapsed,
  lapsedTooltip,
  onRevealed,
}: {
  rows: EvaluationListItem[];
  clientsById: Map<string, ClientRecord>;
  lapsed: boolean;
  lapsedTooltip: string;
  onRevealed: (clientId: string, name: { first: string; last: string }) => void;
}) {
  return (
    <div className="overflow-x-auto rounded-none border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)]">
      <table className="w-full border-collapse text-left text-sm">
        <caption className="sr-only">Your evaluations</caption>
        <thead>
          <tr className="border-b border-[color:var(--bg-quaternary)] text-xs uppercase tracking-wide text-[color:var(--text-tertiary)]">
            <th scope="col" className="px-4 py-3 font-semibold">Client</th>
            <th scope="col" className="px-4 py-3 font-semibold">Date</th>
            <th scope="col" className="px-4 py-3 font-semibold">Current step</th>
            <th scope="col" className="px-4 py-3 font-semibold">Status</th>
            <th scope="col" className="px-4 py-3 font-semibold">Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <EvaluationRow
              key={row.id}
              row={row}
              client={clientsById.get(row.client_id)}
              lapsed={lapsed}
              lapsedTooltip={lapsedTooltip}
              onRevealed={onRevealed}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EvaluationRow({
  row,
  client,
  lapsed,
  lapsedTooltip,
  onRevealed,
}: {
  row: EvaluationListItem;
  client: ClientRecord | undefined;
  lapsed: boolean;
  lapsedTooltip: string;
  onRevealed: (clientId: string, name: { first: string; last: string }) => void;
}) {
  const status = EVALUATION_STATUS_META[row.state];
  return (
    <tr className="border-b border-[color:var(--bg-quaternary)] last:border-b-0 align-top">
      <td className="px-4 py-3">
        <div className="flex flex-col gap-1">
          <ClientNameReveal
            clientId={row.client_id}
            initialErased={Boolean(client?.gdpr_erased_at)}
            erasedAt={client?.gdpr_erased_at ?? null}
            onRevealed={(name) => onRevealed(row.client_id, name)}
          />
          {/* Secondary identifier is the system Client ID (UUID) — the mock's
              "CITS-205029" display code is FR12-deferred and intentionally not shown. */}
          <span className="tabular-nums text-xs text-[color:var(--text-tertiary)]" title={row.client_id}>
            Client ID: {row.client_id}
          </span>
        </div>
      </td>
      <td className="px-4 py-3 align-middle tabular-nums text-[color:var(--text-secondary)]">
        {formatShortDate(row.updated_at ?? row.created_at)}
      </td>
      <td className="px-4 py-3 align-middle text-[color:var(--text-secondary)]">
        {humanizeCurrentStep(row.current_step, row.state)}
      </td>
      <td className="px-4 py-3 align-middle">
        <Badge variant={status.pillVariant}>{status.label}</Badge>
      </td>
      <td className="px-4 py-3 align-middle">
        <EvaluationActionCell row={row} lapsed={lapsed} lapsedTooltip={lapsedTooltip} />
      </td>
    </tr>
  );
}

function EvaluationActionCell({
  row,
  lapsed,
  lapsedTooltip,
}: {
  row: EvaluationListItem;
  lapsed: boolean;
  lapsedTooltip: string;
}) {
  const action = EVALUATION_ACTION[row.state];

  // Story 9.13 shipped the Results library + the scored → results-panel open-into path, so
  // a `scored` row's "Results" now links to /evaluations/{id} via the fall-through below.
  // Only `archived` (View) remains deferred — an archived row is excluded from the default
  // view and has no first-class open target yet — rendered disabled with a neutral tooltip
  // (never a dead link, no Epic/Story number on screen).
  if (action.isDeferred) {
    return (
      <Button variant="secondary" size="sm" disabled title="Archived evaluations aren't available to open yet.">
        {action.label}
      </Button>
    );
  }

  // A write action (Continue) is disabled while the subscription is lapsed (read-only).
  if (action.isWrite && lapsed) {
    return (
      <Button
        variant="secondary"
        size="sm"
        disabled
        title={lapsedTooltip}
      >
        {action.label}
      </Button>
    );
  }

  return (
    <Button asChild variant="secondary" size="sm">
      <Link to={`/evaluations/${row.id}`}>{action.label}</Link>
    </Button>
  );
}

function Pagination({
  page,
  totalPages,
  hasPrevious,
  hasNext,
  isFetching,
  onPrevious,
  onNext,
}: {
  page: number;
  totalPages: number;
  hasPrevious: boolean;
  hasNext: boolean;
  isFetching: boolean;
  onPrevious: () => void;
  onNext: () => void;
}) {
  return (
    <nav className="flex items-center justify-between gap-4" aria-label="Pagination">
      <span className="tabular-nums text-sm text-[color:var(--text-tertiary)]">
        Page {page} of {totalPages}
      </span>
      <div className="flex items-center gap-2">
        <Button variant="secondary" size="sm" onClick={onPrevious} disabled={!hasPrevious || isFetching}>
          <ChevronLeft aria-hidden="true" className="h-4 w-4" />
          Previous
        </Button>
        <Button variant="secondary" size="sm" onClick={onNext} disabled={!hasNext || isFetching}>
          Next
          <ChevronRight aria-hidden="true" className="h-4 w-4" />
        </Button>
      </div>
    </nav>
  );
}

export default EvaluationsListScreen;
